/**
 * Similar to doing `array.filter(f).slice(0, n)`; except that this is better for taking small slices of large arrays
 * because this only runs the filter until the required number of elements have been found.
 */
export function filterLimit<T>(array: T[], filter: (item: T, index: number) => boolean, count: number) {
  const results: T[] = [];

  for (let i = 0; i < array.length; i++) {
    if (filter(array[i], i)) {
      results.push(array[i]);
      if (results.length === count) {
        return results;
      }
    }
  }

  return results;
}

/**
 * Groups elements into a map.
 * Key must be supported by map for this to work (e.g. string, number, boolean)
 */
export function groupBy<TElement, TKey>(elements: Iterable<TElement>, keySelector: (element: TElement) => TKey) {
  const grouped = new Map<TKey, TElement[]>();

  for (const element of elements) {
    const key = keySelector(element);
    grouped.set(key, [
      ...grouped.get(key) ?? [],
      element,
    ]);
  }

  return grouped;
}

/**
 * Groups elements by an equality function (`equal`), then combines groups that occur under the same set of keys.
 * @param equal If provided, uses this function to determine element equality. If not provided, uses `===`.
 * @example
 * const group = [
 *   { group: 'a', value: 1 },
 *   { group: 'a', value: 2 },
 *   { group: 'b', value: 1 },
 *   { group: 'b', value: 1 },
 *   { group: 'b', value: 3 },
 * ];
 *
 * const result = mergedGroupBy(group, e => e.group, (a, b) => a.value === b.value);
 * // result = [
 * //   {
 * //     keys: ['a', 'b'],
 * //     values: [
 * //       { group: 'a', value: 1 },
 * //       { group: 'b', value: 1 },
 * //       { group: 'b', value: 1 },
 * //     ]
 * //   },
 * //   {
 * //     keys: ['a'],
 * //     values: [
 * //       { group: 'a', value: 2 },
 * //     ]
 * //   },
 * //   {
 * //     keys: ['b'],
 * //     values: [
 * //       { group: 'b', value: 3 },
 * //     ]
 * //   },
 * // ]
 */
export function mergedGroupBy<TKey, TElement>(
  elements: Iterable<TElement>,
  keySelector: (element: TElement) => TKey,
  equal?: (a: TElement, b: TElement) => boolean,
) {
  equal ??= ((a, b) => a === b);

  // Simpler to build it this way, than trying to build the output array first, as would be a lot of array shuffling that way
  const groupsForValues: { values: TElement[]; keys: Set<TKey> }[] = [];
  for (const element of elements) {
    const existing = groupsForValues.find(x => equal(x.values[0], element));
    if (existing) {
      existing.values.push(element);
      existing.keys.add(keySelector(element));
    } else {
      groupsForValues.push({ values: [element], keys: new Set([keySelector(element)]) });
    }
  }

  // Coalesce groups with identical key sets
  const groups: { keys: TKey[]; values: TElement[] }[] = [];
  for (const { values, keys } of groupsForValues) {
    // The sort order doesn't actually matter, as long as it consistently gives same output for the same items
    // eslint-disable-next-line sonarjs/no-alphabetical-sort
    const orderedKeys = [...keys].sort();
    const existing = groups.find(g =>
      g.keys.length === orderedKeys.length &&
      g.keys.every((k, i) => k === orderedKeys[i]),
    );
    if (existing) {
      existing.values.push(...values);
    } else {
      groups.push({ keys: orderedKeys, values });
    }
  }

  return groups;
}

/**
 * Returns elements that exist in the left collection, excluding those that exist in the right.
 * @param equal If provided, uses this function to determine element equality. If not provided, uses `===`.
 */
export function except<TElement>(left: TElement[], right: TElement[], equal?: (a: TElement, b: TElement) => boolean) {
  equal ??= ((a, b) => a === b);
  return left.filter(a => !right.some(b => equal(a, b)));
}

/**
 * Returns elements that exist only in both collections.
 * @param equal If provided, uses this function to determine element equality. If not provided, uses `===`.
 */
export function intersect<TElement>(left: TElement[], right: TElement[], equal?: (a: TElement, b: TElement) => boolean) {
  equal ??= ((a, b) => a === b);
  return left.filter(a => right.some(b => equal(a, b)));
}
